import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Loader2, ShieldCheck, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils/cn';

export interface TotpSetupFormProps {
  /** Kratos settings flow (any to dodge the typed coupling with @ory/client). */
  flow: any;
  csrfToken: string;
  identifier: string;
  isSubmitting: boolean;
  submitError?: string | null;
  onSubmit: (code: string) => Promise<void>;
}

/**
 * TOTP setup form rendered inside the settings flow.
 *
 * Reads the TOTP-related nodes from the Kratos flow:
 *  - `totp_qr`     → <img src="data:image/png;base64,…"> (the QR code)
 *  - `totp_secret_key` → string the user can copy when they cannot scan
 *  - `totp_code`   → 6-digit input the user types from their app
 *
 * Submit is delegated to the parent (programmatic fetch), same pattern we
 * use for the password path. Keeps the surface predictable and avoids the
 * cross-origin form-action quirks.
 */
export function TotpSetupForm({
  flow,
  csrfToken,
  identifier,
  isSubmitting,
  submitError,
  onSubmit,
}: TotpSetupFormProps) {
  const { t } = useTranslation();
  const [code, setCode] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const qrNode = flow?.ui?.nodes?.find(
    (n: any) => n.group === 'totp' && n.type === 'img'
  );
  const secretNode = flow?.ui?.nodes?.find(
    (n: any) =>
      n.group === 'totp' && n.attributes?.name === 'totp_secret_key'
  );
  const codeNode = flow?.ui?.nodes?.find(
    (n: any) => n.group === 'totp' && n.attributes?.name === 'totp_code'
  );

  const qrSrc = qrNode?.attributes?.src as string | undefined;
  const secretValue = secretNode?.attributes?.value as string | undefined;

  React.useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const copySecret = async () => {
    if (!secretValue) return;
    try {
      await navigator.clipboard.writeText(secretValue);
      setCopied(true);
    } catch {
      // ignore — non-fatal
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6 || isSubmitting) return;
    void onSubmit(code.trim());
  };

  if (!codeNode) {
    // Flow does not expose totp nodes — the method is probably disabled
    // server-side or the flow already completed enrolment.
    return (
      <Alert>
        <AlertDescription className="text-sm">
          {t('auth.mfaTotpUnavailable')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Hidden fields Kratos expects in the body */}
      <input type="hidden" name="csrf_token" value={csrfToken} readOnly />
      <input type="hidden" name="method" value="totp" readOnly />
      <input type="hidden" name="traits.email" value={identifier} readOnly />

      <div className="rounded-lg border border-input bg-muted/30 p-4">
        <p className="text-xs text-muted-foreground mb-3">
          {t('auth.mfaTotpScanInstructions')}
        </p>

        {qrSrc && (
          <div className="flex justify-center mb-3">
            <img
              src={qrSrc}
              alt={t('auth.mfaTotpQrAlt')}
              className="h-44 w-44 rounded-md bg-white p-2"
            />
          </div>
        )}

        {secretValue && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('auth.mfaTotpSecretManual')}
            </p>
            <div className="flex items-center gap-2">
              <code
                className={cn(
                  'flex-1 select-all rounded-md border border-input bg-background px-3 py-2',
                  'font-mono text-xs tracking-wider break-all'
                )}
              >
                {secretValue}
              </code>
              <button
                type="button"
                onClick={copySecret}
                aria-label={
                  copied ? t('common.copied') : t('common.copyToClipboard')
                }
                className={cn(
                  'inline-flex items-center justify-center rounded-md border border-input',
                  'h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-accent',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  'transition-colors'
                )}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="totp_code" className="text-sm font-medium">
          {t('auth.mfaTotpCodeLabel')}
        </label>
        <input
          id="totp_code"
          name="totp_code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          minLength={6}
          pattern="[0-9]{6}"
          placeholder={t('auth.mfaTotpCodePlaceholder')}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={isSubmitting}
          required
          aria-invalid={submitError ? 'true' : undefined}
          className={cn(
            'flex h-12 w-full rounded-md border border-input bg-background px-3 py-2',
            'text-center font-mono text-lg tracking-[0.5em]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            submitError && 'border-destructive focus-visible:ring-destructive'
          )}
        />
      </div>

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{submitError}</AlertDescription>
        </Alert>
      )}

      <Button
        type="submit"
        className="w-full h-11"
        disabled={isSubmitting || code.length !== 6}
        aria-disabled={isSubmitting || code.length !== 6}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('auth.settingsSubmitting')}
          </>
        ) : (
          <>
            <ShieldCheck className="mr-2 h-4 w-4" />
            {t('auth.mfaTotpVerify')}
          </>
        )}
      </Button>
    </form>
  );
}
